import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { useContext, useState, useEffect, useRef } from 'react'
import { Button, CircularProgress, IconButton, InputAdornment, TextField, LinearProgress } from '@mui/material'

import { countriesCurrencies } from '../lib'
import Dialog from '../components/common/dialog'
import HelpIcon from '../assets/icons/help-icon.svg'
import { AuthContext } from '../contexts/auth.context'
import { UserContext } from '../contexts/user.context'
import SearchIcon from '../assets/icons/search-icon.svg'
import { createBankAccount } from '../api/customers.api'
import { BankAccount, Customer, FormError } from '../types'
import '../styles/individual-money-transfers-management.css'
import CalculateIcon from '../assets/icons/calculate-icon.svg'
import { AppStateContext } from '../contexts/app-state.context'
import { normalizeCustomerData } from '../utils/normalize-data.utils'
import { checkTwoFactorAuth, requestAccessToken } from '../api/auth.api'
import { NotificationsContext } from '../contexts/notifications.context'
import NoBeneficiariesIcon from '../assets/icons/no-beneficiaries-icon.svg'
import { textFieldWhiteBg, baseButtonWhiteBg, baseButtonBlueBg } from '../theme/custom-styles'
import { getCustomersDataWithSameBankAccount, searchCustomersByRegex } from '../api/app-queries.api'
import CustomerDataForm from '../components/customers-management/customer-data-form'
import BankAccountForm from '../components/bank-accounts-management/bank-account-form'
import { ExchangeCalculatorForm } from '../components/clients-balances-management/exchange-calculator-form'
import { MexicanCustomerPaymentForm } from '../components/customer-companies-management/mexican-customer-payment-form'
import { buildTokens, handleCheckIfBankAccountNumberIsValid, handleCheckIfOtpCodeIsValid, handleSyncDataToLocalDB, matchesAllTokens, normalize } from '../utils'
import { sendReviewedPayment, sendReviewedPaymentByCurrency, sendTransactionToReviewByCurrency } from '../api/payments.api'
import { IndividualBeneficiaryItem } from '../components/individual-money-transfers-management/individual-beneficiary-item'
import { MakeIndividualPaymentForm } from '../components/individual-money-transfers-management/make-individual-payment-form'
import { AgentinianCustomerPaymentForm } from '../components/customer-companies-management/argentinian-customer-payment-form'
import { ChileanCustomerPaymentForm } from '../components/customer-companies-management/chilean-customer-payment-form'
import { PeruvianCustomerPaymentForm } from '../components/customer-companies-management/peruvian-customer-payment-form'
import { BrazilianCustomerPaymentForm } from '../components/customer-companies-management/brazilian-customer-payment-form'

const IndividualMoneyTransfersManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

  const navigate = useNavigate()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { banksData, defaultPocketIdSelected, processingBatch, setCustomersBriefData, customersBriefData, isLoading: appDataLoading } = useContext(AppStateContext)
  const { t } = useTranslation([
    'individual-money-transfers-management',
    'two-factor-auth',
    'payments-to-review-management',
    'bank-accounts-management',
    'customers-management',
    'common',
    'customer-company-details-management'
  ])

  const customerToSearchRef = useRef<string>('')
  const componentDidMount = useRef<boolean>(false)
  const updatingCustomerData = useRef<boolean>(false)
  const currentLoadingDataRef = useRef<boolean>(true)
  const noRegistriesFoundAtDatabaseRef = useRef<boolean>(false)
  const currentCustomersDataStateRef = useRef<Array<Partial<Customer>>>([])

  const [otpCode, setOtpCode] = useState('')
  const [errors, setErrors] = useState<FormError>({})
  const [amountToSend, setAmountToSend] = useState<string>('')
  const [selectedPocketId, setSelectedPocketId] = useState<string>('')
  const [customerToSearch, setCustomerToSearch] = useState<string>('')
  const [loadingCustomersData, setLoadingCustomersData] = useState(true)
  const [searchedInDatabase, setSearchedInDatabase] = useState<boolean>(false)
  const [openNewBankAccountModal, setOpenNewBankAccountModal] = useState(false)
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [bankAccountData, setBankAccountData] = useState<Partial<BankAccount>>({})
  const [openNewPaymentModal, setOpenNewPaymentModal] = useState<boolean>(false)
  const [customersList, setCustomersList] = useState<Array<Partial<Customer>>>([])
  const [customersData, setCustomersData] = useState<Array<Partial<Customer>>>([])
  const [selectedBankAccountData, setSelectedBankAccountData] = useState<string>('')
  const [bankAccountsToSelect, setBankAccountsToSelect] = useState<Array<string>>([])
  const [dialogNewPaymentIsLoading, setDialogNewPaymentIsLoading] = useState<boolean>(false)
  const [dialogCalculateYourFundsIsOpen, setDialogCalculateYourFundsIsOpen] = useState(false)
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)
  const [customerSelectedData, setCustomerSelectedData] = useState<Partial<Customer> | null>(null)
  const [dialogNewBankAccountIsLoading, setDialogNewBankAccountIsLoading] = useState<boolean>(false)
  const [openSendPaymentConfimationModal, setOpenSendPaymentConfimationModal] = useState<boolean>(false)
  const [openNewIndividualMoneyTransferModal, setOpenNewIndividualMoneyTransferModal] = useState<boolean>(false)
  const [dialogSendPaymentConfimationIsLoading, setDialogSendPaymentConfimationIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (customersBriefData?.customersData) {
      handleSetCustomersByCountryInitialData(customersBriefData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersBriefData])

  useEffect(() => {
    if (!appDataLoading && loadingCustomersData && !customersBriefData?.customersData) {
      setLoadingCustomersData(false)
    }
  }, [appDataLoading, customersBriefData, loadingCustomersData])

  const handleSetCustomersByCountryInitialData = async (dataToSet: any) => {
    if (dataToSet.customersData) {
      const currentCustomerToSearchState = customerToSearchRef.current
      const customersNormalizedData = dataToSet.customersData.map((customerData: Partial<Customer>) => normalizeCustomerData(customerData)) as unknown as Array<Customer | Partial<Customer>>
      customersNormalizedData.sort((thisCustomer: Partial<Customer>, nextCustomer: Partial<Customer>) => {
        return Date.parse(String(nextCustomer.createdAt)) - Date.parse(String(thisCustomer.createdAt))
      })

      if (currentCustomerToSearchState === '') {
        const lastestCustomersCount = handleGetLastestCustomersCount()
        const lastestCustomersDataUpdated: Array<Customer> = customersNormalizedData.slice(0, lastestCustomersCount)

        if (processingBatch === false) {
          for (const element of lastestCustomersDataUpdated) {
            const customerData = element
            handleSyncDataToLocalDB('customers', customerData)
          }
        }

        setCustomersList(lastestCustomersDataUpdated)
      } else {
        const customersDataFiltered = await handleGetDataWithRegexSearch(customersNormalizedData)

        if (processingBatch === false) {
          for (const element of customersDataFiltered) {
            const customerData = element
            handleSyncDataToLocalDB('customers', customerData)
          }
        }

        console.log('customersDataFiltered', customersDataFiltered, currentCustomerToSearchState)
        setCustomersList(customersDataFiltered)
      }

      currentCustomersDataStateRef.current = customersNormalizedData
      setCustomersData(customersNormalizedData as Array<Customer>)

      if (currentLoadingDataRef.current === true) {
        currentLoadingDataRef.current = false
        setLoadingCustomersData(false)
      }

      if (updatingCustomerData.current === true) {
        const selectedCustomerDataUpdated = customersNormalizedData.find((customerData: Customer) => String(customerData._id) === String(customerSelectedData?._id))

        if (selectedCustomerDataUpdated) {
          const customerBankAccountsToSelect = Array.isArray(selectedCustomerDataUpdated.bankAccountsData) ? [...selectedCustomerDataUpdated.bankAccountsData] : []
          const bankAccountsToSelectToDisplay = []

          for (const element of customerBankAccountsToSelect) {
            const bankAccountData = element
            bankAccountsToSelectToDisplay.push(`${bankAccountData.bankName}-${bankAccountData.bankNumber}-${bankAccountData.bankAchCode}-${bankAccountData.bankAccountType}-${bankAccountData.bankAccountMain}`)

            if (bankAccountData.bankAccountMain) {
              setSelectedBankAccountData(`${bankAccountData.bankName}-${bankAccountData.bankNumber}-${bankAccountData.bankAchCode}-${bankAccountData.bankAccountType}-${bankAccountData.bankAccountMain}`)
            }
          }

          setCustomerSelectedData(selectedCustomerDataUpdated)
          setBankAccountsToSelect(bankAccountsToSelectToDisplay)
        }

        updatingCustomerData.current = false
      }

      if (componentDidMount.current === false) {
        componentDidMount.current = true
      }
    }
  }

  const handleGetLastestCustomersCount = () => {
    // if (
    //   (config.environment !== 'production' &&
    //     (userSession?.role.includes('-client') ||
    //       (userSession?.role.includes('-client') && userSession.belongsTo === config.app.paxumCompanyId) ||
    //       (userSession?.role.includes('-client') && userSession.belongsTo === config.app.demoPaxumCompanyId))) ||
    //   (config.environment === 'production' && (userSession?.role.includes('-client') || (userSession?.role.includes('-client') && userSession.belongsTo === config.app.paxumCompanyId))) ||
    //   userSession?.role.includes('builder')
    // ) {
    //   return 3
    // }

    if (!userSession?.role.includes('-client')) {
      return 3
    }

    return 5
  }

  const handleGetMaxHeightOfModule = () => {
    if (!userSession?.role.includes('-client')) {
      return 240
    }

    return 410
  }

  const handleGetDataWithRegexSearch = async (currentCustomersData: Array<Customer>) => {
    const customerToSearchData = customerToSearchRef.current

    // const customersDataFilteredBySearch = currentCustomersData.filter((customerData) => {
    //   const regexToSearch = new RegExp(String(customerToSearchData), 'gi')

    //   if (regexToSearch.test(String(customerData.registeredFullName))) {
    //     return customerData
    //   }

    //   if (regexToSearch.test(String(customerData?.documentNumber))) {
    //     return customerData
    //   }

    //   let searchedByBankAccountNumber = false

    //   if (Array.isArray(customerData.bankAccountsData)) {
    //     for (const element of customerData.bankAccountsData) {
    //       const bankAccountData = element

    //       if (regexToSearch.test(String(bankAccountData.bankNumber))) {
    //         searchedByBankAccountNumber = true
    //       }
    //     }

    //     if (searchedByBankAccountNumber) {
    //       return customerData
    //     }
    //   }

    //   return false
    // })

    const customersDataFilteredBySearch = currentCustomersData.filter((customerData) => {
      const tokens = buildTokens(customerToSearchData)
      if (tokens.length === 0) return true

      const fullName = normalize(customerData.registeredFullName)
      const docNumber = normalize(customerData.documentNumber)
      const bankNumbers = Array.isArray(customerData.bankAccountsData) ? customerData.bankAccountsData.map((b) => normalize(b?.bankNumber)) : []

      if (matchesAllTokens(fullName, tokens)) return true
      if (matchesAllTokens(docNumber, tokens)) return true
      if (bankNumbers.some((bn) => matchesAllTokens(bn, tokens))) return true

      return false
    })

    return customersDataFilteredBySearch
  }

  const handleValidateNewCustomerForm = async () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}

    if (!customerSelectedData?.firstName) {
      formDataIsValid = false
      formErrors.firstName = t('newCustomerModal.customerDataForm.errors.emptyFirstName', { ns: 'customers-management' })
    }

    if (!customerSelectedData?.lastName) {
      formDataIsValid = false
      formErrors.lastName = t('newCustomerModal.customerDataForm.errors.emptyLastName', { ns: 'customers-management' })
    }

    if (!customerSelectedData?.documentType) {
      formDataIsValid = false
      formErrors.documentType = t('newCustomerModal.customerDataForm.errors.emptyDocumentType', { ns: 'customers-management' })
    }

    if (!customerSelectedData?.documentNumber) {
      formDataIsValid = false
      formErrors.documentNumber = t('newCustomerModal.customerDataForm.errors.emptyDocumentNumber', { ns: 'customers-management' })
    }

    if (selectedCountry === 'COL') {
      if (
        !Array.isArray(customerSelectedData?.bankAccountsData) ||
        (Array.isArray(customerSelectedData?.bankAccountsData) &&
          customerSelectedData?.bankAccountsData.length > 0 &&
          customerSelectedData?.bankAccountsData[0] &&
          !customerSelectedData?.bankAccountsData[0].bankAccountType)
      ) {
        formDataIsValid = false
        formErrors.bankAccountType = t('newCustomerModal.customerDataForm.errors.emptyBankAccountType', { ns: 'customers-management' })
      }

      if (
        !Array.isArray(customerSelectedData?.bankAccountsData) ||
        (Array.isArray(customerSelectedData?.bankAccountsData) &&
          customerSelectedData?.bankAccountsData.length > 0 &&
          customerSelectedData?.bankAccountsData[0] &&
          customerSelectedData?.bankAccountsData[0].bankAccountType !== 'breb' &&
          !customerSelectedData?.bankAccountsData[0].bankName)
      ) {
        formDataIsValid = false
        formErrors.bankName = t('newCustomerModal.customerDataForm.errors.emptyBankName', { ns: 'customers-management' })
      }

      if (
        !Array.isArray(customerSelectedData?.bankAccountsData) ||
        (Array.isArray(customerSelectedData?.bankAccountsData) && customerSelectedData?.bankAccountsData.length > 0 && customerSelectedData?.bankAccountsData[0] && !customerSelectedData?.bankAccountsData[0].bankNumber)
      ) {
        formDataIsValid = false
        formErrors.bankAccountNumber = t('newCustomerModal.customerDataForm.errors.emptyBankAccountNumber', { ns: 'customers-management' })
      }
    }

    if (selectedCountry === 'MEX') {
      if (
        !Array.isArray(customerSelectedData?.bankAccountsData) ||
        (Array.isArray(customerSelectedData?.bankAccountsData) &&
          customerSelectedData?.bankAccountsData.length > 0 &&
          customerSelectedData?.bankAccountsData[0] &&
          !customerSelectedData?.bankAccountsData[0].bankAccountType)
      ) {
        formDataIsValid = false
        formErrors.bankAccountType = t('newCustomerModal.customerDataForm.errors.emptyBankAccountType', { ns: 'customers-management' })
      }

      if (
        !Array.isArray(customerSelectedData?.bankAccountsData) ||
        (Array.isArray(customerSelectedData?.bankAccountsData) && customerSelectedData?.bankAccountsData.length > 0 && customerSelectedData?.bankAccountsData[0] && !customerSelectedData?.bankAccountsData[0].bankNumber)
      ) {
        formDataIsValid = false
        formErrors.bankAccountNumber = t('newCustomerModal.customerDataForm.errors.emptyBankAccountNumber', { ns: 'customers-management' })
      }
    }

    if (selectedCountry === 'ARG' || selectedCountry === 'CHI' || selectedCountry === 'PER') {
      if (
        !Array.isArray(customerSelectedData?.bankAccountsData) ||
        (Array.isArray(customerSelectedData?.bankAccountsData) &&
          customerSelectedData?.bankAccountsData.length > 0 &&
          customerSelectedData?.bankAccountsData[0] &&
          !customerSelectedData?.bankAccountsData[0].bankAccountType)
      ) {
        formDataIsValid = false
        formErrors.bankAccountType = t('newCustomerModal.customerDataForm.errors.emptyBankAccountType', { ns: 'customers-management' })
      }

      if (
        !Array.isArray(customerSelectedData?.bankAccountsData) ||
        (Array.isArray(customerSelectedData?.bankAccountsData) &&
          customerSelectedData?.bankAccountsData.length > 0 &&
          customerSelectedData?.bankAccountsData[0] &&
          customerSelectedData?.bankAccountsData[0].bankAccountType !== 'breb' &&
          !customerSelectedData?.bankAccountsData[0].bankName)
      ) {
        formDataIsValid = false
        formErrors.bankName = t('newCustomerModal.customerDataForm.errors.emptyBankName', { ns: 'customers-management' })
      }

      if (
        !Array.isArray(customerSelectedData?.bankAccountsData) ||
        (Array.isArray(customerSelectedData?.bankAccountsData) && customerSelectedData?.bankAccountsData.length > 0 && customerSelectedData?.bankAccountsData[0] && !customerSelectedData?.bankAccountsData[0].bankNumber)
      ) {
        formDataIsValid = false
        formErrors.bankAccountNumber = t('newCustomerModal.customerDataForm.errors.emptyBankAccountNumber', { ns: 'customers-management' })
      }
    }

    if (userSession?.role === 'super-admin-client' && !selectedPocketId) {
      formDataIsValid = false
      formErrors.selectedPocketId = t('newCustomerModal.customerDataForm.errors.emptySelectedPocket', { ns: 'customers-management' })
    }

    if (!amountToSend) {
      formDataIsValid = false
      formErrors.amountToSend = t('paymentForm.errors.paymentAmountEmpty')
    }

    if (!formDataIsValid) {
      return setErrors(formErrors)
    }

    const customerIsAlreadyAdded = customersData?.find((customerData) => String(customerData.documentNumber) === String(customerSelectedData?.documentNumber))

    if (customerIsAlreadyAdded) {
      setErrors({
        generalError: t('newCustomerModal.customerDataForm.errors.customerAlreadyAdded', { ns: 'customers-management' })
      })
      return
    }

    setOpenSendPaymentConfimationModal(true)
  }

  const handleSetCustomerToSearch = async (customerToSearchData: string) => {
    setErrors({})
    setSearchingInDatabase(true)
    setCustomerToSearch(customerToSearchData)
    customerToSearchRef.current = customerToSearchData

    if (noRegistriesFoundAtDatabaseRef.current === true) {
      setNoRegistriesFoundAtDatabase(false)
    }

    if (customerToSearchData === '') {
      setSearchedInDatabase(false)
      setSearchingInDatabase(false)
      customerToSearchRef.current = ''

      if (Array.isArray(currentCustomersDataStateRef?.current)) {
        const lastestCustomersCount = handleGetLastestCustomersCount()
        const lastestCustomersDataUpdated: Array<Customer> = currentCustomersDataStateRef?.current.slice(0, lastestCustomersCount)

        setCustomersList(lastestCustomersDataUpdated)
      }

      // return handleGetCustomersData()
      return
    }

    if (Array.isArray(currentCustomersDataStateRef?.current) && customerToSearchRef.current.length >= 2) {
      const customersDataFiltered = await handleGetDataWithRegexSearch(currentCustomersDataStateRef?.current)

      if (processingBatch === false) {
        for (const element of customersDataFiltered) {
          const customerData = element
          // await handleSyncDataToLocalDB('customers', customerData)
          handleSyncDataToLocalDB('customers', customerData)
        }
      }

      setSearchedInDatabase(true)
      setSearchingInDatabase(false)
      setCustomersList(customersDataFiltered)
    }
  }

  const handleValidatePaymentToSendForm = async () => {
    setErrors({})
    let formDataIsValid = true
    let extractedAmountToSend = null
    const formErrors: FormError = {}

    if (!amountToSend) {
      formDataIsValid = false
      formErrors.amountToSend = t('paymentForm.errors.paymentAmountEmpty')
    }

    if (amountToSend) {
      extractedAmountToSend = amountToSend === '$' ? Number(amountToSend.slice(1).replaceAll(',', '').replaceAll('$', '').trim()) : Number(amountToSend.trim().replaceAll(',', '').replaceAll('$', '').trim())

      if (Number.isNaN(Number(extractedAmountToSend)) || Number(extractedAmountToSend) <= 0) {
        formDataIsValid = false
        formErrors.amountToSend = t('paymentForm.errors.paymentAmountWrong')
      }
    }

    if (!selectedBankAccountData && Array.isArray(customerSelectedData?.bankAccountsData) && customerSelectedData?.bankAccountsData.length === 0) {
      formDataIsValid = false
      formErrors.selectedBankAccount = t('paymentForm.errors.selectedEmptyBankAccount')
    }

    if (!selectedBankAccountData && Array.isArray(customerSelectedData?.bankAccountsData) && customerSelectedData?.bankAccountsData.length >= 1) {
      formDataIsValid = false
      formErrors.selectedBankAccount = t('paymentForm.errors.selectedBankAccount')
    }

    if (userSession?.role === 'super-admin-client' && !selectedPocketId) {
      formDataIsValid = false
      formErrors.selectedPocketId = t('paymentForm.errors.emptySelectedPocket')
    }

    // if (userSession?.role.includes('-builder')) {
    //   formDataIsValid = false
    //   formErrors.generalError = t('paymentForm.errors.notAuthorized')
    // }

    if (!formDataIsValid) {
      return setErrors(formErrors)
    }

    setOpenSendPaymentConfimationModal(true)
  }

  const handleValidateNewBankAccountForm = async () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}
    const bankAccountAlreadyAdded = Array.isArray(customerSelectedData?.bankAccountsData)
      ? customerSelectedData?.bankAccountsData.find((bankAccountItemData: BankAccount) => bankAccountItemData.bankNumber === bankAccountData.bankNumber)
      : undefined

    if (!bankAccountData.bankAccountType) {
      formDataIsValid = false
      formErrors.bankAccountType = t('newBankAccountModal.bankAccountForm.errors.emptyBankAccountType', { ns: 'bank-accounts-management' })
    }

    if (selectedCountry === 'ARG' || selectedCountry === 'CHI' || selectedCountry === 'PER' || selectedCountry === 'BRA' || (selectedCountry === 'COL' && bankAccountData.bankAccountType !== 'breb')) {
      if (!bankAccountData.bankName) {
        formDataIsValid = false
        formErrors.bankName = t('newBankAccountModal.bankAccountForm.errors.emptyBankName', { ns: 'bank-accounts-management' })
      }
    }

    if (!bankAccountData.bankNumber) {
      formDataIsValid = false
      formErrors.bankAccountNumber = t('newBankAccountModal.bankAccountForm.errors.emptyBankAccountNumber', { ns: 'bank-accounts-management' })
    }

    if (bankAccountData.bankNumber && bankAccountAlreadyAdded) {
      formDataIsValid = false
      formErrors.bankAccountNumber = t('newBankAccountModal.bankAccountForm.errors.duplicateBankAccountNumber', { ns: 'bank-accounts-management' })
    }

    if (selectedCountry === 'COL' && bankAccountData.bankAccountType !== 'breb') {
      if (bankAccountData.bankNumber && Number.isNaN(Number(bankAccountData.bankNumber.trim()))) {
        formDataIsValid = false
        formErrors.bankAccountNumber = t('bankAccountNoveltyForm.errorMessages.hasToBeOnlyDigits', { ns: 'payments-to-review-management' })
      }

      if (bankAccountData.bankNumber && !Number.isNaN(Number(bankAccountData.bankNumber.trim()))) {
        const bankAccountNumberIsInvalid = handleCheckIfBankAccountNumberIsValid(bankAccountData as { bankAchCode: string }, bankAccountData.bankNumber.trim(), t)

        if (bankAccountNumberIsInvalid) {
          formDataIsValid = false
          formErrors.bankAccountNumber = bankAccountNumberIsInvalid
        }
      }
    }

    if (selectedCountry === 'BRA') {
      if (!bankAccountData.pix) {
        formDataIsValid = false
        formErrors.pixKey = t('newBankAccountModal.bankAccountForm.errors.emptyPixKey', { ns: 'bank-accounts-management' })
      }
    }

    if (!formDataIsValid) {
      return setErrors(formErrors)
    }

    setDialogNewBankAccountIsLoading(true)
    const {
      data: { accessToken }
    } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
    const { data } = await getCustomersDataWithSameBankAccount(String(userSession?.belongsTo), String(customerSelectedData?._id), String(bankAccountData.bankNumber?.trim()), accessToken)

    if (Number.isNaN(Number(data)) || (!Number.isNaN(Number(data)) && data > 0)) {
      formDataIsValid = false
      formErrors.bankAccountNumber = t('bankAccountNoveltyForm.errorMessages.duplicateBankAccount', { ns: 'payments-to-review-management' })
    }

    if (!formDataIsValid) {
      setDialogNewBankAccountIsLoading(false)
      return setErrors(formErrors)
    }

    try {
      const bankAccountDataToCreate = {
        ...bankAccountData,
        bankAccountMain: Array.isArray(customerSelectedData?.bankAccountsData) && customerSelectedData?.bankAccountsData.length === 0 ? true : bankAccountData.bankAccountMain
      }
      updatingCustomerData.current = true
      await createBankAccount(bankAccountDataToCreate, accessToken, String(customerSelectedData?._id))

      setBankAccountData({})
      setOpenNewBankAccountModal(false)
      setDialogNewBankAccountIsLoading(false)
      updateNewNotificationStatus?.(true, 'bank-account-created')
    } catch (error: any) {
      console.log('Error handleValidateNewCustomerForm', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401 && error?.response?.data.message === 'Not authorized: Exists') {
        setErrors({ generalError: `${t('formGeneralErrors.unauthorized', { ns: 'common' })}: 406` })
      } else if (error?.response?.status === 401 && !(error?.response?.data.message === 'Not authorized: Exists')) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogNewBankAccountIsLoading(false)
    }
  }

  const handleSendPayment = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setErrors({})
      setDialogSendPaymentConfimationIsLoading(true)
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessTokenData.data.accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      return setDialogSendPaymentConfimationIsLoading(false)
    }

    try {
      setErrors({})
      setOtpCode('')
      const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()
      const amountToSendAsNumber = amountToSend === '$' ? Number(amountToSend.slice(1).replaceAll(',', '').replaceAll('$', '').trim()) : Number(amountToSend.trim().replaceAll(',', '').replaceAll('$', '').trim())
      const selectedPocketIdToSet = userSession?.role === 'super-admin-client' ? selectedPocketId : userSession?.role === 'admin-client' || userSession?.role === 'admin-client-builder' ? defaultPocketIdSelected : ''

      if (!selectedPocketIdToSet) {
        console.log('selectedPocketIdToSetError', selectedPocketIdToSet)
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
        return
      }

      // console.log({ fromApi: false, currency, customerSelectedData, selectedBankAccountData, amountToSend: amountToSendAsNumber, selectedPocketId: selectedPocketIdToSet })
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const {
        data: { savedPaymentData }
      } = await sendTransactionToReviewByCurrency({ fromApi: false, currency, customerSelectedData, selectedBankAccountData, amountToSend: amountToSendAsNumber, selectedPocketId: selectedPocketIdToSet }, accessToken)

      if (savedPaymentData.status === 'reviewed') {
        if (userSession?.role.indexOf('builder') === -1) {
          const {
            data: { accessToken }
          } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')

          if (currency === 'cop') {
            await sendReviewedPayment(String(savedPaymentData._id), accessToken)
          } else {
            await sendReviewedPaymentByCurrency(String(savedPaymentData._id), currency, accessToken)
          }
        }

        navigate(`/customer-company-payments/${userSession?.belongsTo}`)
        updateNewNotificationStatus?.(true, 'payment-sent-successfully')
      } else {
        setOpenSendPaymentConfimationModal(false)
        setOpenNewIndividualMoneyTransferModal(false)
        setDialogSendPaymentConfimationIsLoading(false)

        navigate('/payments-to-review')
        updateNewNotificationStatus?.(true, 'payment-sent-with-novelties')
      }
    } catch (error: any) {
      console.log(error, error?.response?.data?.message, Object.keys(error))
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.insufficientAdamoBalance', { ns: 'common' }) })
      } else if (error?.response?.status === 400) {
        setErrors({ generalError: t('formGeneralErrors.insufficientBalance', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogSendPaymentConfimationIsLoading(false)
    }
  }

  const handleSearchCustomerDataByRegex = async () => {
    try {
      setErrors({})
      setSearchingInDatabase(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      const { data } = await searchCustomersByRegex({ searchText: customerToSearch, belongsTo: userSession?.belongsTo, selectedCountry, customerCompanyPocketId: defaultPocketIdSelected }, accessToken)

      if (data.customersData.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        noRegistriesFoundAtDatabaseRef.current = true
        return
      }

      const incomingCustomers = [...data.customersData]
      const existingIds = new Set<string>(customersData.map((customerData: Customer) => String(customerData._id)))
      const customersUpdatedData = incomingCustomers.filter((p) => !existingIds.has(p._id))
      const customerDataToSet = [...customersData, ...customersUpdatedData]
      customerDataToSet.sort((thisCustomer: Partial<Customer>, nextCustomer: Partial<Customer>) => {
        return Date.parse(String(nextCustomer.createdAt)) - Date.parse(String(thisCustomer.createdAt))
      })

      setCustomersBriefData({ customersData: customerDataToSet })
      currentCustomersDataStateRef.current = customerDataToSet
      setCustomersData(customerDataToSet)
      setCustomersList(data.customersData)
      setSearchingInDatabase(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="individual-money-transfers-content">
      <div className="individual-money-transfers-container">
        <div className="individual-money-transfers-title-text-container">
          <div style={userSession && userSession?.role.indexOf('-client-builder') > -1 ? { display: 'flex', gap: '15px' } : {}}>
            <h1 className="individual-money-transfers-title">{t('homeModule.title')}</h1>
            {userSession && userSession?.role.indexOf('-client-builder') > -1 && (
              <div
                className="exchange-rate-calculator-button-container"
                onClick={() => setDialogCalculateYourFundsIsOpen(true)}
              >
                <img
                  src={CalculateIcon}
                  alt="calculate-icon"
                />
              </div>
            )}
          </div>
          <p className="individual-money-transfers-main-instruction">{t('homeModule.mainInstruction')}</p>
        </div>
        {loadingCustomersData && (
          <div className="individual-money-transfers-no-beneficiaries-warning-container">
            <CircularProgress />
            <div className="individual-money-transfers-no-beneficiaries-warning-text">{t('warningTexts.loadingCustomersData')}</div>
          </div>
        )}
        {!loadingCustomersData && customersData.length === 0 && (
          <div className="individual-money-transfers-no-beneficiaries-warning-container">
            <img
              src={NoBeneficiariesIcon}
              alt="no-beneficiaries-icon"
            />
            <div className="individual-money-transfers-no-beneficiaries-warning-text">{t('warningTexts.noBeneficiariesData')}</div>
          </div>
        )}
        {!loadingCustomersData && customersData.length > 0 && (
          <>
            <div className="individual-money-transfers-beneficiary-searcher-container">
              <TextField
                fullWidth
                variant="standard"
                sx={textFieldWhiteBg}
                label={t('searchField.placeholder')}
                value={customerToSearch.toUpperCase()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    handleSetCustomerToSearch(customerToSearch.toUpperCase())
                  }
                }}
                onChange={(event) => handleSetCustomerToSearch(event.target.value.toUpperCase())}
                onBlur={() => {
                  if (customerToSearchRef.current === '') {
                    handleSetCustomerToSearch('')
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        aria-label="toggle password visibility"
                        sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                      >
                        <img
                          src={SearchIcon}
                          alt="search-icon"
                        />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </div>
            {searchingInDatabase ? (
              <>
                <div className="individual-money-transfers-searching-progress-bar">
                  <LinearProgress />
                </div>
                <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.searchingInDatabase')}</div>
              </>
            ) : (
              <>
                {searchedInDatabase && customersList.length === 0 ? (
                  <>
                    {noRegistriesFoundAtDatabase === false ? (
                      <div style={{ gap: '20px', alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
                        <div className="customer-no-data-found-no-registries-text">
                          {t('searchingResultTexts.case1.secondPart', { ns: 'customer-company-details-management' })}{' '}
                          <strong
                            className="customer-no-data-found-no-registries-text-click"
                            onClick={handleSearchCustomerDataByRegex}
                          >
                            {t('searchingResultTexts.case1.thirdPart', { ns: 'customer-company-details-management' })}
                          </strong>{' '}
                          {t('searchingResultTexts.case1.fourthPart', { ns: 'customer-company-details-management' })}
                        </div>
                        <Button
                          sx={baseButtonBlueBg}
                          onClick={handleSearchCustomerDataByRegex}
                        >
                          {t('searchingResultTexts.expandSearch', { ns: 'customer-company-details-management' })}
                        </Button>
                        {errors.generalError && (
                          <div
                            style={{ marginTop: '0px' }}
                            className="error-message"
                          >
                            {errors.generalError}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ gap: '20px', alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
                        <div className="individual-money-transfers-beneficiary-subtitle">{t('homeModule.subtitleSearchNotFound')}</div>
                        <div
                          className="customer-no-data-found-no-registries-text"
                          style={{ display: 'flex', justifyContent: 'center' }}
                        >
                          <img
                            src={HelpIcon}
                            alt="help-icon"
                          />
                        </div>
                        <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2', { ns: 'customers-management' })}</div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {searchedInDatabase ? (
                      <div className="individual-money-transfers-beneficiary-subtitle">{t('homeModule.subtitleSearch')}</div>
                    ) : (
                      <div className="individual-money-transfers-beneficiary-subtitle">{t('homeModule.subtitle')}</div>
                    )}
                    <div
                      style={{ maxHeight: `${handleGetMaxHeightOfModule()}px` }}
                      className="individual-money-transfers-latest-beneficiaries-container"
                    >
                      {customersList.map((customerData: Partial<Customer>) => {
                        return (
                          <IndividualBeneficiaryItem
                            key={String(customerData._id)}
                            customerData={customerData}
                            handleOpenMakeIndividualPayment={(selectedCustomerData: Customer) => {
                              const customerBankAccountsToSelect = Array.isArray(selectedCustomerData.bankAccountsData) ? [...selectedCustomerData.bankAccountsData] : []
                              const bankAccountsToSelectToDisplay = []

                              for (const element of customerBankAccountsToSelect) {
                                const bankAccountData = element
                                bankAccountsToSelectToDisplay.push(
                                  `${bankAccountData.bankName}-${bankAccountData.bankNumber}-${bankAccountData.bankAchCode}-${bankAccountData.bankAccountType}-${bankAccountData.bankAccountMain}`
                                )

                                if (bankAccountData.bankAccountMain) {
                                  setSelectedBankAccountData(
                                    `${bankAccountData.bankName}-${bankAccountData.bankNumber}-${bankAccountData.bankAchCode}-${bankAccountData.bankAccountType}-${bankAccountData.bankAccountMain}`
                                  )
                                }
                              }

                              setBankAccountsToSelect(bankAccountsToSelectToDisplay)
                              setCustomerSelectedData((prevCustomerDataState: Customer | null) => {
                                const newCustomerDataState = { ...selectedCustomerData }

                                setOpenNewIndividualMoneyTransferModal(true)

                                return {
                                  ...prevCustomerDataState,
                                  ...newCustomerDataState
                                }
                              })
                            }}
                          />
                        )
                      })}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
        <div className="individual-money-transfers-action-button">
          <Button
            variant="contained"
            sx={baseButtonWhiteBg}
            onClick={() => {
              handleSetCustomerToSearch('')
              setOpenNewPaymentModal(true)
            }}
          >
            {t('buttonActionsTexts.newPayment')}
          </Button>
        </div>
      </div>
      {dialogCalculateYourFundsIsOpen && (
        <Dialog
          hideActionsButtons
          dialogTitle={t('calculateYourFundsModal.title', { ns: 'banks-balances-management' })}
          handleDialogOpenState={(openState: boolean) => setDialogCalculateYourFundsIsOpen(openState)}
        >
          <ExchangeCalculatorForm />
        </Dialog>
      )}
      {openNewPaymentModal && (
        <Dialog
          errors={errors}
          dialogTitle={t('newPaymentModal.title', { ns: 'customers-management' })}
          actionButtonText={userSession?.role.includes('-builder') ? t('newPaymentModal.sendToBeApproved', { ns: 'customers-management' }) : t('newPaymentModal.sendPayment', { ns: 'customers-management' })}
          dialogIsLoading={dialogNewPaymentIsLoading}
          handleDialogAction={handleValidateNewCustomerForm}
          handleDialogOpenState={(openState: boolean) => {
            setOpenNewPaymentModal(openState)

            if (openState === false) {
              setErrors({})
              setSelectedPocketId('')
              setCustomerSelectedData(null)
            }
          }}
        >
          {selectedCountry === 'COL' && (
            <CustomerDataForm
              errors={errors}
              banksData={banksData ?? []}
              amountToSend={amountToSend}
              selectedPocketId={selectedPocketId}
              customerData={customerSelectedData}
              handleSetCustomerData={(newCustomerData: Partial<Customer>) => {
                setErrors({})
                setCustomerSelectedData((prevCustomerData: Partial<Customer> | null) => ({
                  ...prevCustomerData,
                  ...newCustomerData
                }))
              }}
              handleSetPocketId={(pocketIdToSet: string) => setSelectedPocketId(pocketIdToSet)}
              handleSetPaymentAmountToSend={(paymentAmountToSend: string) => setAmountToSend(paymentAmountToSend)}
            />
          )}
          {selectedCountry === 'MEX' && (
            <MexicanCustomerPaymentForm
              errors={errors}
              selectedCurrency="MXN"
              amountToSend={amountToSend}
              customerData={customerSelectedData}
              selectedPocketId={selectedPocketId}
              handleSetPocketId={(pocketIdToSet: string) => setSelectedPocketId(pocketIdToSet)}
              handleSetPaymentAmountToSend={(paymentAmountToSend: string) => setAmountToSend(paymentAmountToSend)}
              handleSetCustomerData={(attributeToSet: string, valueToSet: string | Array<BankAccount>) => {
                setCustomerSelectedData((prevCustomerData: Partial<Customer> | null) => ({
                  ...prevCustomerData,
                  [attributeToSet]: valueToSet
                }))
              }}
            />
          )}
          {selectedCountry === 'ARG' && (
            <AgentinianCustomerPaymentForm
              errors={errors}
              customerData={customerSelectedData}
              amountToSend={amountToSend}
              selectedPocketId={selectedPocketId}
              selectedCurrency="ARS"
              handleSetPocketId={(pocketIdToSet: string) => setSelectedPocketId(pocketIdToSet)}
              handleSetPaymentAmountToSend={(paymentAmountToSend: string) => setAmountToSend(paymentAmountToSend)}
              handleSetCustomerData={(attributeToSet: string, valueToSet: string | Array<BankAccount>) => {
                setCustomerSelectedData((prevCustomerData: Partial<Customer> | null) => ({
                  ...prevCustomerData,
                  [attributeToSet]: valueToSet
                }))
              }}
            />
          )}
          {selectedCountry === 'CHI' && (
            <ChileanCustomerPaymentForm
              errors={errors}
              customerData={customerSelectedData}
              amountToSend={amountToSend}
              selectedPocketId={selectedPocketId}
              selectedCurrency="CLP"
              handleSetPocketId={(pocketIdToSet: string) => setSelectedPocketId(pocketIdToSet)}
              handleSetPaymentAmountToSend={(paymentAmountToSend: string) => setAmountToSend(paymentAmountToSend)}
              handleSetCustomerData={(attributeToSet: string, valueToSet: string | Array<BankAccount>) => {
                setCustomerSelectedData((prevCustomerData: Partial<Customer> | null) => ({
                  ...prevCustomerData,
                  [attributeToSet]: valueToSet
                }))
              }}
            />
          )}
          {selectedCountry === 'PER' && (
            <PeruvianCustomerPaymentForm
              errors={errors}
              customerData={customerSelectedData}
              amountToSend={amountToSend}
              selectedPocketId={selectedPocketId}
              selectedCurrency="PEN"
              handleSetPocketId={(pocketIdToSet: string) => setSelectedPocketId(pocketIdToSet)}
              handleSetPaymentAmountToSend={(paymentAmountToSend: string) => setAmountToSend(paymentAmountToSend)}
              handleSetCustomerData={(attributeToSet: string, valueToSet: string | Array<BankAccount>) => {
                setCustomerSelectedData((prevCustomerData: Partial<Customer> | null) => ({
                  ...prevCustomerData,
                  [attributeToSet]: valueToSet
                }))
              }}
            />
          )}
          {selectedCountry === 'BRA' && (
            <BrazilianCustomerPaymentForm
              errors={errors}
              customerData={customerSelectedData}
              amountToSend={amountToSend}
              selectedPocketId={selectedPocketId}
              selectedCurrency="BRL"
              handleSetPocketId={(pocketIdToSet: string) => setSelectedPocketId(pocketIdToSet)}
              handleSetPaymentAmountToSend={(paymentAmountToSend: string) => setAmountToSend(paymentAmountToSend)}
              handleSetCustomerData={(attributeToSet: string, valueToSet: string | Array<BankAccount>) => {
                setCustomerSelectedData((prevCustomerData: Partial<Customer> | null) => ({
                  ...prevCustomerData,
                  [attributeToSet]: valueToSet
                }))
              }}
            />
          )}
        </Dialog>
      )}
      {openNewIndividualMoneyTransferModal && (
        <Dialog
          errors={errors}
          dialogIsLoading={dialogNewPaymentIsLoading}
          handleDialogAction={handleValidatePaymentToSendForm}
          handleDialogOpenState={(openState: boolean) => {
            setOpenNewIndividualMoneyTransferModal(openState)

            if (openState === false) {
              setErrors({})
              setAmountToSend('')
              setSelectedPocketId('')
              setCustomerSelectedData(null)
              setSelectedBankAccountData('')
            }
          }}
          dialogTitle={t('newIndividualMoneyTransferModal.title')}
          actionButtonText={userSession?.role.includes('-builder') ? t('newPaymentModal.sendToBeApproved', { ns: 'customers-management' }) : t('newIndividualMoneyTransferModal.actionButtonText')}
        >
          <MakeIndividualPaymentForm
            errors={errors}
            amountToSend={amountToSend}
            customerData={customerSelectedData}
            selectedPocketId={selectedPocketId}
            bankAccountsToSelect={bankAccountsToSelect}
            selectedBankAccountData={selectedBankAccountData}
            handleOpenNewBankAccountDialog={() => setOpenNewBankAccountModal(true)}
            handleSetPocketId={(pocketIdToSet: string) => setSelectedPocketId(pocketIdToSet)}
            handleSetPaymentAmountToSend={(paymentAmountToSend: string) => setAmountToSend(paymentAmountToSend)}
            handleSetBankAccountToSend={(bankAccountToSend: string) => setSelectedBankAccountData(bankAccountToSend)}
          />
        </Dialog>
      )}
      {openNewBankAccountModal && (
        <Dialog
          errors={errors}
          dialogIsLoading={dialogNewBankAccountIsLoading}
          handleDialogAction={handleValidateNewBankAccountForm}
          handleDialogOpenState={(openState: boolean) => {
            setOpenNewBankAccountModal(openState)

            if (openState === false) {
              setErrors({})
              setBankAccountData({})
            }
          }}
          dialogTitle={t('newBankAccountModal.title', { ns: 'bank-accounts-management' })}
          actionButtonText={t('newBankAccountModal.actionButtonText', { ns: 'bank-accounts-management' })}
        >
          <BankAccountForm
            errors={errors}
            banksData={banksData ?? []}
            bankAccountData={bankAccountData}
            handleSetBankAccountData={(bankAccountDataToAdd: BankAccount) => {
              setBankAccountData({ ...bankAccountDataToAdd })
            }}
            mainBankAccountAsDefault={Array.isArray(customerSelectedData?.bankAccountsData) && customerSelectedData?.bankAccountsData.length === 0}
          />
        </Dialog>
      )}
      {openSendPaymentConfimationModal && (
        <Dialog
          errors={errors}
          actionButtonWarningStyle
          handleDialogAction={handleSendPayment}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setOtpCode('')
              setDialogNewPaymentIsLoading(false)
            }

            setOpenSendPaymentConfimationModal(openState)
          }}
          dialogIsLoading={dialogSendPaymentConfimationIsLoading}
          dialogTitle={t('confirmSendPaymentModal.title', { ns: 'payments-to-review-management' })}
          actionButtonText={t('confirmSendPaymentModal.actionButtonText', { ns: 'payments-to-review-management' })}
        >
          <div
            className="step"
            style={{ marginTop: '0px', gap: '16px' }}
          >
            <div
              className="step-instruction-container"
              style={{ fontSize: '14px', color: '#F59E0B' }}
            >
              {t('checkCodeInstruction', { ns: 'two-factor-auth' })}
            </div>
            <MuiOtpInput
              length={6}
              value={otpCode}
              onChange={(newValue: string) => setOtpCode(newValue)}
              className={errors?.otpCodeError ? 'MuiOtpInput-Box-inner MuiOtpInput-Box-error' : 'MuiOtpInput-Box-inner'}
            />
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default IndividualMoneyTransfersManagement
